import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

/**
 * KlaviyoWaitlistInline
 * Inline Waitlist form for product pages
 * Shows when product variant is out of stock
 * @element klaviyo-waitlist-inline
 */
export class KlaviyoWaitlistInline extends UcoastEl {
	static htmlSelector = 'klaviyo-waitlist-inline'
	static selectors = {
		form: 'form',
		header: '.klaviyo-waitlist-inline__header',
		emailInput: '[name="email"]',
		submitButton: '[type="submit"]',
		successMessage: '[data-success-message]',
		errorMessage: '[data-error-message]',
	}

	form: HTMLFormElement
	headerEl?: HTMLElement
	emailInput: HTMLInputElement
	submitButton: HTMLButtonElement
	successMessageEl?: HTMLElement
	errorMessageEl?: HTMLElement
	companyId: string
	productId: string
	productTitle: string
	variantId: string
	variantTitle: string
	buttonText: string
	loadingText: string

	constructor() {
		super()
		this.form = q.rs(KlaviyoWaitlistInline.selectors.form, this)
		this.headerEl =
			q.os(KlaviyoWaitlistInline.selectors.header, this) ?? undefined
		this.emailInput = q.rs(KlaviyoWaitlistInline.selectors.emailInput, this)
		this.submitButton = q.rs(
			KlaviyoWaitlistInline.selectors.submitButton,
			this
		)
		this.successMessageEl = q.os(
			KlaviyoWaitlistInline.selectors.successMessage,
			this
		)
		this.errorMessageEl = q.os(
			KlaviyoWaitlistInline.selectors.errorMessage,
			this
		)

		// Get data attributes
		this.companyId = q.ra(this, 'data-company-id')
		this.productId = q.ra(this, 'data-product-id')
		this.productTitle = q.ra(this, 'data-product-title')
		this.variantId = q.ra(this, 'data-variant-id')
		this.variantTitle = q.ra(this, 'data-variant-title')
		this.buttonText = this.getAttribute('data-button-text') || 'NOTIFY ME'
		this.loadingText =
			this.getAttribute('data-loading-text') || 'Submitting...'

		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
	}

	private static workerUrl =
		'https://pvolve-klaviyo-bis-worker.refactorthis.workers.dev/subscribe'

	private getEnvironment(): string {
		const hostname = window.location.hostname
		return hostname === 'pvolve.com' || hostname === 'www.pvolve.com'
			? 'production'
			: 'staging'
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()

		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		if (this.emailInput.value.length < 1) return
		if (!this.isValidEmail(this.emailInput.value)) {
			this.showError('Please enter a valid email address')
			return
		}

		this.setLoadingState(true)

		const payload = {
			email: this.emailInput.value,
			productId: this.productId,
			variantId: this.variantId,
			productTitle: this.productTitle,
			variantTitle: this.variantTitle,
			productUrl: window.location.href,
			environment: this.getEnvironment(),
		}

		fetch(KlaviyoWaitlistInline.workerUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})
			.then(async (response) => {
				let result
				try {
					result = await response.json()
				} catch {
					throw new Error('Invalid response from server')
				}
				if (!response.ok || !result.success) {
					throw new Error(
						result.error || result.message || 'Something went wrong'
					)
				}
			})
			.then(() => {
				this.showSuccess()
			})
			.catch((e) => {
				console.error('[KlaviyoWaitlistInline] Submit failed:', e)
				this.showError('Something went wrong. Please try again.')
			})
			.finally(() => {
				this.setLoadingState(false)
			})
	}

	private isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	private setLoadingState(isLoading: boolean) {
		if (isLoading) {
			this.submitButton.setAttribute('aria-disabled', 'true')
			this.submitButton.classList.add('loading')
			this.submitButton.textContent = this.loadingText
		} else {
			this.submitButton.removeAttribute('aria-disabled')
			this.submitButton.classList.remove('loading')
			this.submitButton.textContent = this.buttonText
		}
	}

	private showSuccess() {
		this.form.classList.add('hidden')
		if (this.headerEl) {
			this.headerEl.classList.add('hidden')
		}
		if (this.successMessageEl) {
			this.successMessageEl.classList.remove('hidden')
			this.successMessageEl.classList.add('flex')
		}

		// Dispatch custom event for other components to listen
		this.dispatchEvent(
			new CustomEvent('klaviyo-waitlist:success', {
				detail: {
					email: this.emailInput.value,
					productId: this.productId,
					variantId: this.variantId,
				},
				bubbles: true,
			})
		)
	}

	private showError(message: string) {
		if (this.errorMessageEl) {
			this.errorMessageEl.textContent = message
			this.errorMessageEl.classList.remove('hidden')
		}

		// Dispatch custom event
		this.dispatchEvent(
			new CustomEvent('klaviyo-waitlist:error', {
				detail: { message },
				bubbles: true,
			})
		)

		// Hide error after 5 seconds
		window.setTimeout(() => {
			if (this.errorMessageEl) {
				this.errorMessageEl.classList.add('hidden')
			}
		}, 5000)
	}

	/**
	 * Update the variant ID when variant changes
	 * Called by product page when variant selector changes
	 */
	public updateVariant(variantId: string, variantTitle: string) {
		this.variantId = variantId
		this.variantTitle = variantTitle
		this.setAttribute('data-variant-id', variantId)
		this.setAttribute('data-variant-title', variantTitle)
	}
}
